import React, { forwardRef } from 'react';
import { StatusAlertSmall, StatusWarningSmall } from '@wix/wix-ui-icons-common';

import LabelledElement from '../LabelledElement/LabelledElement';
import type { InputImperativeActions } from '../Input';
import Input from '../Input';

import Text from '../Text';

import { classes, st } from './InputWithLabel.st.css';
import { DATA_HOOKS } from './InputWithLabel.constants';
import { InputWithLabelProps } from './InputWithLabel.types';
import { useIcons } from '../WixDesignSystemIconThemeProvider';

const getSuffixContainer = (suffix: React.ReactNode[]) =>
  suffix.map((item: React.ReactNode, index: number) => {
    return (
      <div
        data-hook="suffix-container"
        key={`suffix-container-${index}`}
        className={classes.groupIcon}
      >
        {item}
      </div>
    );
  });

const InputWithLabel = forwardRef<InputImperativeActions, InputWithLabelProps>(
  (
    {
      label,
      suffix,
      value,
      dataHook,
      status,
      statusMessage = '',
      onChange,
      onFocus,
      onBlur,
      name,
      type,
      ariaLabel,
      autoFocus,
      autocomplete,
      disabled,
      className,
      maxLength,
      placeholder,
      customInput,
      border,
      ...rest
    },
    ref,
  ) => {
    const icons = useIcons('InputWithLabel', {
      StatusAlertSmall,
      StatusWarningSmall,
    });

    const isBottomLineBorder = border === 'bottomLine';
    const isWarning = status === Input.StatusWarning;
    const showStatusSection =
      (status === Input.StatusError || isWarning) && statusMessage;
    return (
      <div
        data-hook={dataHook}
        className={status ? undefined : classes.statusMessagePlaceholder}
      >
        <LabelledElement
          value={value}
          label={label}
          dataHook={DATA_HOOKS.labelledElement}
          topLabelGray={isBottomLineBorder}
          disabled={disabled}
        >
          {({
            onChange: inputOnChange,
            onFocus: inputOnFocus,
            onBlur: inputOnBlur,
            className: inputClassName,
            getPlaceholder,
            ...restInputProps
          }) => (
            <Input
              className={st(classes.root, inputClassName, className)}
              ref={ref}
              name={name}
              type={type}
              ariaLabel={ariaLabel}
              autoFocus={autoFocus}
              autocomplete={autocomplete}
              disabled={disabled}
              maxLength={maxLength}
              placeholder={getPlaceholder(placeholder)}
              onChange={e => {
                inputOnChange(e);
                onChange?.(e);
              }}
              onFocus={e => {
                inputOnFocus();
                onFocus?.(e);
              }}
              onBlur={e => {
                inputOnBlur();
                onBlur?.(e);
              }}
              dataHook={DATA_HOOKS.input}
              size="large"
              value={value}
              suffix={suffix && getSuffixContainer(suffix)}
              status={status}
              statusMessage={
                <span style={{ color: '#fff' }}>{statusMessage}</span>
              }
              customInput={customInput}
              border={border}
              hideStatusSuffix
              {...rest}
              {...restInputProps}
            />
          )}
        </LabelledElement>
        {showStatusSection && (
          <Text
            skin={isWarning ? 'standard' : 'error'}
            size="small"
            weight={isWarning ? 'thin' : 'normal'}
            className={classes.statusMessage}
          >
            <span
              className={st(classes.statusMessageIcon, { warning: isWarning })}
            >
              {isWarning ? (
                <icons.StatusWarningSmall />
              ) : (
                <icons.StatusAlertSmall />
              )}
            </span>
            <span
              data-hook={DATA_HOOKS.errorMessage}
              className={classes.errorMessageContent}
            >
              {statusMessage}
            </span>
          </Text>
        )}
      </div>
    );
  },
);

InputWithLabel.displayName = 'InputWithLabel';

export default InputWithLabel;
